<template>
  <div class="product-management">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <span>商品管理</span>
          <el-button class="btn-primary" icon="el-icon-plus" @click="openCreate">添加商品</el-button>
        </div>
      </template>

      <div class="product-filter-bar">
        <span class="filter-label">分类筛选</span>
        <el-select
          v-model="categoryFilter"
          placeholder="全部分类"
          clearable
          size="small"
          class="pm-category-filter"
          popper-class="pm-category-filter-dropdown"
          style="width: 180px;"
        >
          <el-option label="全部分类" value="" />
          <el-option
            v-for="c in categoryFilterOptions"
            :key="c.value || c"
            :label="c.label || c"
            :value="c.value || c"
          />
        </el-select>
        <span class="filter-summary">
          共 {{ filteredProducts.length }} / {{ products.length }} 件
        </span>
      </div>

      <el-table :data="filteredProducts" style="width: 100%" class="product-table">
        <el-table-column prop="id" label="商品ID"></el-table-column>
        <el-table-column label="商品图片" width="100">
          <template slot-scope="scope">
            <el-image
              :src="scope.row.image_url"
              style="width: 60px; height: 60px; border-radius: 6px;"
              fit="cover"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="商品名称"></el-table-column>
        <el-table-column prop="price" label="价格"></el-table-column>
        <el-table-column prop="category" label="分类"></el-table-column>
        <el-table-column prop="stock" label="库存" width="80"></el-table-column>
        <el-table-column label="状态" width="96">
          <template slot-scope="scope">
            <el-tag
              size="mini"
              :type="saleStatusTagType(scope.row.sale_status)"
              disable-transitions
            >{{ saleStatusLabel(scope.row.sale_status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="320">
          <template slot-scope="scope">
            <div class="pm-row-actions">
              <el-dropdown
                size="small"
                trigger="click"
                @command="(cmd) => setSaleStatus(scope.row, cmd)"
              >
                <el-button size="small" class="btn-secondary pm-action-status">
                  状态<i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="on" :disabled="normalizeSaleStatus(scope.row.sale_status) === 'on'">上架</el-dropdown-item>
                  <el-dropdown-item command="sold_out" :disabled="normalizeSaleStatus(scope.row.sale_status) === 'sold_out'">售罄</el-dropdown-item>
                  <el-dropdown-item command="off" :disabled="normalizeSaleStatus(scope.row.sale_status) === 'off'">下架</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-button
                size="small"
                class="btn-secondary pm-action-edit"
                title="编辑商品"
                @click="editProduct(scope.row)"
              >
                <i class="el-icon-edit" />
                编辑
              </el-button>
              <el-button
                size="small"
                class="btn-secondary danger pm-action-del"
                title="删除商品"
                @click="deleteProduct(scope.row.id)"
              >
                <i class="el-icon-delete" />
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="dialogMode === 'create' ? '添加商品' : '编辑商品'"
      :visible.sync="dialogVisible"
      width="920px"
      top="3vh"
      destroy-on-close
      append-to-body
      custom-class="app-admin-dialog product-form-dialog"
    >
      <el-form
        :key="formFormKey"
        :model="form"
        :rules="rules"
        ref="productForm"
        label-position="top"
        class="product-form"
      >
        <section class="pf-section">
          <header class="pf-section-head">
            <h4>基本信息</h4>
            <p>名称、分类与展示内容</p>
          </header>
          <div class="pf-grid pf-grid-2">
            <el-form-item label="商品名称" prop="name">
              <el-input v-model="form.name" maxlength="50" show-word-limit placeholder="例如：减盐周餐" />
            </el-form-item>
            <el-form-item label="商品分类" prop="category">
              <el-select v-model="form.category" placeholder="请选择分类" style="width: 100%;" filterable>
                <el-option v-for="c in categoryOptions" :key="c" :label="c" :value="c" />
              </el-select>
            </el-form-item>
          </div>
          <el-form-item v-if="form.category !== '随心订'" label="基础售价（元/份）" prop="price" class="pf-base-price-item">
            <div class="base-price-field">
              <span class="base-price-prefix">¥</span>
              <el-input
                v-model="form.price"
                placeholder="例如：25.00"
                class="base-price-input"
              />
              <span class="base-price-suffix">/份</span>
            </div>
            <p class="pf-hint base-price-hint">用户最终支付价 = 基础售价 + 各板块选中选项加价之和</p>
          </el-form-item>
          <el-form-item v-if="isSpecConfigCategory" label="履约方式" class="pf-fulfillment-item">
            <div class="fulfillment-checks">
              <el-checkbox v-model="form.support_pickup" class="fulfillment-check">门店自提</el-checkbox>
              <el-checkbox v-model="form.support_delivery" class="fulfillment-check">外送</el-checkbox>
            </div>
            <p class="pf-hint">默认勾选门店自提；未勾选外送时，用户下单点「外送」将提示暂不支持外送</p>
          </el-form-item>
        </section>

        <section v-if="form.category === '随心订'" class="pf-section pf-section-accent">
          <header class="pf-section-head">
            <h4>随心订套餐</h4>
            <p>套餐类型 × 餐次时段决定总餐次与自动总价</p>
          </header>

          <el-form-item label="套餐类型">
            <div class="pkg-type-grid">
              <button
                v-for="opt in packageTypeOptions"
                :key="opt.value"
                type="button"
                class="pkg-type-card"
                :class="{ active: form.subscription_package_type === opt.value }"
                @click="selectPackageType(opt.value)"
              >
                <span class="pkg-type-name">{{ opt.label }}</span>
                <span class="pkg-type-meta">{{ opt.meta }}</span>
              </button>
            </div>
          </el-form-item>

          <div class="pf-preset-row">
            <div class="pf-preset-chips">
              <span class="chip">周 {{ presetDefaults.week.base_meals }} 天</span>
              <span class="chip">月 {{ presetDefaults.month.base_meals }} 天</span>
              <span class="chip">季 {{ presetDefaults.quarter.base_meals }} 天</span>
              <span class="chip">年 {{ presetDefaults.year.base_meals }} 天</span>
            </div>
            <el-button type="text" class="pf-link-btn" @click="$router.push('/config/product-mgmt')">去配置修改</el-button>
            <el-button type="text" class="pf-link-btn" @click="openPresetDialog">快速修改基数</el-button>
          </div>

          <el-form-item label="允许单选餐次">
            <el-switch
              v-model="form.subscription_allow_single_meal"
              active-text="开启"
              inactive-text="关闭"
            />
            <p class="pf-hint">开启后，小程序顾客可只选午餐或只选晚餐；关闭后须与下方「餐次时段」全选一致。</p>
          </el-form-item>

          <el-form-item label="餐次时段">
            <div class="meal-slot-toggles">
              <button
                type="button"
                class="meal-slot-btn"
                :class="{ active: form.meal_lunch }"
                @click="toggleMealSlot('lunch')"
              >
                <i class="el-icon-sunny" />
                午餐
              </button>
              <button
                type="button"
                class="meal-slot-btn"
                :class="{ active: form.meal_dinner }"
                @click="toggleMealSlot('dinner')"
              >
                <i class="el-icon-moon" />
                晚餐
              </button>
            </div>
            <p class="pf-hint">双选系数 2，单选系数 1；影响总餐次与总价。</p>
          </el-form-item>

          <div class="pf-grid pf-grid-2 pf-price-pair">
            <el-form-item label="单餐单价" prop="unit_meal_price" class="pf-base-price-item">
              <div class="base-price-field base-price-field--full">
                <span class="base-price-prefix">¥</span>
                <el-input
                  v-model="form.unit_meal_price"
                  class="base-price-input"
                  placeholder="例如：18"
                  @input="recalcSubscriptionPrice"
                />
                <span class="base-price-suffix">/单餐</span>
              </div>
            </el-form-item>
            <el-form-item
              v-if="form.subscription_package_type === 'custom'"
              label="总购买餐次"
              prop="subscription_total_meals"
              class="pf-base-price-item"
            >
              <div class="base-price-field base-price-field--full">
                <el-input-number
                  v-model="form.subscription_total_meals"
                  class="base-price-number"
                  :min="1"
                  :max="9999"
                  :step="1"
                  :precision="0"
                  controls-position="right"
                  @change="recalcSubscriptionPrice"
                />
                <span class="base-price-suffix">餐</span>
              </div>
            </el-form-item>
            <el-form-item v-else label="总购买餐次" class="pf-base-price-item">
              <div class="base-price-field base-price-field--full base-price-field--readonly">
                <span class="pf-readonly-meals-value">{{ form.subscription_total_meals || 0 }}</span>
                <span class="base-price-suffix">餐</span>
              </div>
              <p class="pf-hint">由套餐类型与餐次时段自动计算</p>
            </el-form-item>
            <el-form-item label="可选档位" class="pf-tiers-item">
              <el-input
                v-model="form.subscription_meal_tiers_text"
                class="pf-field-input"
                placeholder="固定档位，逗号分隔，如 10,20,30；留空则仅当前总餐次"
                @change="onMealTiersTextChange"
              />
              <div class="pf-hint" style="margin-top:6px;">顾客下单时从这些档位中选择；不填则只显示上方总购买餐次。</div>
            </el-form-item>
          </div>

          <div class="pf-price-summary">
            <div class="pf-price-main">
              <span class="pf-price-label">自动总价</span>
              <strong class="pf-price-value">¥{{ form.price || '0.00' }}</strong>
            </div>
            <div class="pf-price-formula">
              {{ form.subscription_total_meals || 0 }} 餐 × ¥{{ form.unit_meal_price || 0 }}
            </div>
            <el-form-item label="商品售价（可覆盖）" prop="price" class="pf-price-override pf-base-price-item">
              <div
                class="base-price-field base-price-field--full"
                :class="{ 'base-price-field--disabled': !!form.unit_meal_price }"
              >
                <span class="base-price-prefix">¥</span>
                <el-input
                  v-model="form.price"
                  class="base-price-input"
                  placeholder="自动计算"
                  :disabled="!!form.unit_meal_price"
                />
              </div>
            </el-form-item>
          </div>
        </section>

        <section class="pf-section">
          <header class="pf-section-head">
            <h4>库存与展示</h4>
            <p>库存、图片与商品描述</p>
          </header>
          <div class="pf-media-row">
            <el-form-item label="商品图片" class="pf-upload-item">
              <el-upload
                class="avatar-uploader"
                :show-file-list="false"
                :http-request="handleImageUpload"
                accept="image/jpeg,image/png,image/webp"
              >
                <img v-if="form.image_url" :src="form.image_url" class="avatar" />
                <div v-else class="avatar-placeholder">
                  <i class="el-icon-plus" />
                  <span>上传图片</span>
                </div>
              </el-upload>
              <p class="pf-hint">建议 800×600（4:3）或 1:1，JPG/PNG/WebP</p>
            </el-form-item>
            <div class="pf-media-fields">
              <el-form-item label="库存" prop="stock">
                <el-input v-model="form.stock" placeholder="例如：100" />
              </el-form-item>
              <el-form-item label="销售状态" prop="sale_status">
                <el-radio-group v-model="form.sale_status">
                  <el-radio label="on">上架</el-radio>
                  <el-radio label="sold_out">售罄</el-radio>
                  <el-radio label="off">下架</el-radio>
                </el-radio-group>
                <p class="pf-hint">下架：小程序不展示；售罄：仍展示但不可购买</p>
              </el-form-item>
              <el-form-item label="描述">
                <el-input
                  type="textarea"
                  :rows="4"
                  v-model="form.description"
                  maxlength="500"
                  show-word-limit
                  placeholder="可选，用于小程序商品详情"
                />
              </el-form-item>
            </div>
          </div>
        </section>

        <section v-if="isSpecConfigCategory" class="pf-section pf-section-spec">
          <header class="pf-section-head">
            <h4>规格板块配置</h4>
            <p>
              板块名称与选项名称统一从「配置 → 商品管理配置」拉取；选择板块后自动带入该板块下已上架选项。
              商品保存后：最少/最多选择、选项列表、加价以本页为准；之后再保存配置，不会覆盖这些字段（配置只同步选项图片与库存状态）。
              若在本页删减了某板块的选项，保存后会记住，配置保存不会加回来。
              若要恢复全部选项：先清空板块名称，再重新选择一次该板块。
              总价 = 基础价 + 选中选项加价之和
            </p>
          </header>

          <div class="pf-grid pf-grid-2 spec-meta-grid">
            <el-form-item label="展示标签">
              <el-input
                v-model="form.product_tags"
                maxlength="100"
                placeholder="多个用逗号分隔，如：专业营养师推荐,超值换购"
              />
            </el-form-item>
            <el-form-item label="主食材">
              <el-input v-model="form.main_ingredient" maxlength="50" placeholder="例如：鸡胸肉" />
            </el-form-item>
            <el-form-item label="克重">
              <el-input v-model="form.weight_text" maxlength="30" placeholder="例如：450g" />
            </el-form-item>
            <el-form-item label="热量">
              <el-input v-model="form.calories_text" maxlength="30" placeholder="例如：520kcal" />
            </el-form-item>
            <el-form-item label="Tips" class="spec-tips-item">
              <el-input v-model="form.tips" maxlength="200" placeholder="例如：建议搭配轻食饮品" />
            </el-form-item>
          </div>

          <div
            v-for="(group, gIndex) in form.spec_groups"
            :key="'sg-' + gIndex"
            class="spec-group-card"
            draggable="true"
            @dragstart="onGroupDragStart(gIndex, $event)"
            @dragover.prevent
            @drop="onGroupDrop(gIndex, $event)"
          >
            <div class="spec-group-toolbar">
              <div class="spec-group-title-row">
                <span class="spec-group-badge">{{ gIndex + 1 }}</span>
                <div class="spec-group-title-text">
                  <span class="spec-group-index">板块 {{ gIndex + 1 }}</span>
                  <span class="spec-group-style-tag">圆角按钮 · 统一样式</span>
                </div>
              </div>
              <div class="spec-group-actions">
                <button
                  type="button"
                  class="spec-icon-btn"
                  :disabled="gIndex === 0"
                  @click="moveSpecGroup(gIndex, -1)"
                >上移</button>
                <button
                  type="button"
                  class="spec-icon-btn"
                  :disabled="gIndex >= form.spec_groups.length - 1"
                  @click="moveSpecGroup(gIndex, 1)"
                >下移</button>
                <button
                  type="button"
                  class="spec-icon-btn danger"
                  @click="removeSpecGroup(gIndex)"
                >删除</button>
              </div>
            </div>

            <div class="pf-grid pf-grid-2">
              <el-form-item label="板块名称" required>
                <el-select
                  v-model="group.group_name"
                  filterable
                  clearable
                  placeholder="从配置选择板块名称（可输入筛选）"
                  style="width: 100%"
                  @change="(val) => onSpecGroupNameChange(gIndex, val)"
                >
                  <el-option
                    v-for="tpl in enabledSpecTemplates"
                    :key="tpl.id || tpl.group_name"
                    :label="tpl.group_name"
                    :value="tpl.group_name"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="选择规则">
                <div class="spec-rule-hint">
                  最多 1 份 = 单选；最多 &gt;1 份时连点累加（显示 x2）
                </div>
              </el-form-item>
              <el-form-item label="最少选择份数">
                <el-input-number
                  v-model="group.min_select"
                  :min="0"
                  :max="99"
                  controls-position="right"
                  class="spec-full-number"
                />
              </el-form-item>
              <el-form-item label="最多选择份数">
                <el-input-number
                  v-model="group.max_select"
                  :min="0"
                  :max="99"
                  controls-position="right"
                  class="spec-full-number"
                  placeholder="0=不限制"
                />
                <p class="pf-hint">留空或 0 表示不限制上限</p>
              </el-form-item>
            </div>

            <div class="spec-options-panel">
              <div class="spec-options-head">
                <div>
                  <span class="spec-options-title">板块选项</span>
                  <span class="spec-options-count">{{ (group.options && group.options.length) || 0 }} 项</span>
                </div>
                <el-button
                  type="primary"
                  plain
                  size="small"
                  icon="el-icon-plus"
                  class="spec-add-opt-btn"
                  @click="addSpecOption(gIndex)"
                >添加选项</el-button>
              </div>

              <div
                v-for="(opt, oIndex) in group.options"
                :key="'so-' + gIndex + '-' + oIndex"
                class="spec-option-card"
              >
                <div class="spec-option-top">
                  <span class="spec-option-index">选项 {{ oIndex + 1 }}</span>
                  <button
                    type="button"
                    class="spec-icon-btn danger"
                    @click="removeSpecOption(gIndex, oIndex)"
                  >删除</button>
                </div>

                <div class="spec-option-grid">
                  <div class="spec-field spec-field-name">
                    <label>选项名称</label>
                    <el-select
                      v-model="opt.option_name"
                      filterable
                      clearable
                      placeholder="从配置选择（可输入筛选）"
                      style="width: 100%"
                      @change="(val) => onSpecOptionNameChange(gIndex, oIndex, val)"
                    >
                      <el-option
                        v-for="cand in optionCandidatesForGroup(group)"
                        :key="cand.option_name"
                        :label="cand.option_name + (Number(cand.stock_status) === 0 ? '（售罄）' : '')"
                        :value="cand.option_name"
                      />
                    </el-select>
                  </div>
                  <div class="spec-field spec-field-price">
                    <label>加价（元）</label>
                    <div class="spec-price-wrap">
                      <span class="spec-price-yen">¥</span>
                      <el-input-number
                        v-model="opt.extra_price"
                        :min="0"
                        :precision="2"
                        :step="1"
                        controls-position="right"
                        class="spec-price-input"
                      />
                    </div>
                  </div>
                  <div class="spec-field spec-field-sort">
                    <label>排序</label>
                    <el-input-number
                      v-model="opt.sort"
                      :min="0"
                      :max="999"
                      controls-position="right"
                      class="spec-sort-input"
                    />
                  </div>
                  <div class="spec-field spec-field-stock">
                    <label>库存状态</label>
                    <el-select v-model="opt.stock_status" class="spec-stock-select">
                      <el-option :value="1" label="正常" />
                      <el-option :value="0" label="今日售罄" />
                    </el-select>
                  </div>
                  <div class="spec-field spec-field-flags">
                    <label>标记</label>
                    <button
                      type="button"
                      class="spec-recommend-chip"
                      :class="{ active: !!opt.is_recommend }"
                      @click="opt.is_recommend = !opt.is_recommend"
                    >
                      <i :class="opt.is_recommend ? 'el-icon-star-on' : 'el-icon-star-off'" />
                      <span>推荐</span>
                    </button>
                  </div>
                  <div class="spec-field spec-field-image">
                    <label>选项图片</label>
                    <div class="spec-opt-media">
                      <el-upload
                        class="spec-opt-upload"
                        :show-file-list="false"
                        :http-request="(param) => handleOptionImageUpload(param, gIndex, oIndex)"
                        accept="image/jpeg,image/png,image/webp"
                      >
                        <div v-if="opt.option_image" class="spec-opt-thumb-wrap">
                          <img :src="resolveImg(opt.option_image)" class="spec-opt-thumb" />
                          <span class="spec-opt-thumb-tip">更换</span>
                        </div>
                        <div v-else class="spec-opt-upload-btn">
                          <i class="el-icon-plus" />
                          <span>上传</span>
                        </div>
                      </el-upload>
                      <button
                        v-if="opt.option_image"
                        type="button"
                        class="spec-opt-clear"
                        title="移除图片"
                        @click.stop="opt.option_image = ''"
                      >
                        <i class="el-icon-close" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <p v-if="!(group.options && group.options.length)" class="spec-empty-hint">
                暂无选项：选择板块名称后将自动带入配置中的选项，也可手动添加
              </p>
            </div>
          </div>

          <button type="button" class="spec-add-group-btn" @click="addSpecGroup">
            <i class="el-icon-plus" />
            <span>添加板块</span>
          </button>
        </section>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="btn-secondary" @click="dialogVisible = false">取消</el-button>
        <el-button class="btn-primary" :loading="saving" @click="submit">
          {{ dialogMode === 'create' ? '添加商品' : '保存修改' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="修改套餐默认餐次基数"
      :visible.sync="presetDialogVisible"
      width="440px"
      append-to-body
      custom-class="app-admin-dialog"
    >
      <el-form label-position="top" class="preset-form">
        <div class="pf-grid pf-grid-2">
          <el-form-item label="周餐基数"><el-input-number v-model="presetForm.week" :min="1" :max="999" style="width:100%" /></el-form-item>
          <el-form-item label="月餐基数"><el-input-number v-model="presetForm.month" :min="1" :max="999" style="width:100%" /></el-form-item>
          <el-form-item label="季餐基数"><el-input-number v-model="presetForm.quarter" :min="1" :max="999" style="width:100%" /></el-form-item>
          <el-form-item label="年餐基数"><el-input-number v-model="presetForm.year" :min="1" :max="9999" style="width:100%" /></el-form-item>
        </div>
      </el-form>
      <p class="pf-hint">基数 × 时段系数（1 或 2）= 总购买餐次；周末/节假日由系统自动跳过。</p>
      <span slot="footer">
        <el-button @click="presetDialogVisible = false">取消</el-button>
        <el-button class="btn-primary" @click="savePresetDefaults">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fetchProducts, createProduct, updateProduct, deleteProductApi } from '../api/products';
import { formatApiError } from '../utils/apiError';

/** 与小程序「订购套餐」分类 Tab 一致（可被后台配置覆盖） */
const FALLBACK_CATEGORY_OPTIONS = ['随心订', '超级碗', '下午茶', '小食', '饮品']
const FALLBACK_SPEC_CONFIG_CATEGORIES = ['超级碗', '增肌随心碗', '饮品', '随心订']

function emptySpecOption() {
  return {
    option_name: '',
    option_image: '',
    extra_price: 0,
    is_recommend: false,
    stock_status: 1,
    sort: 0
  }
}

function emptySpecGroup() {
  return {
    group_name: '',
    _prev_group_name: '',
    min_select: 1,
    max_select: 1,
    select_type: 'radio',
    sort: 0,
    excluded_options: [],
    options: [emptySpecOption()]
  }
}

function optionFromTemplate(o, sort) {
  return {
    option_name: String((o && o.option_name) || '').trim(),
    option_image: String((o && o.option_image) || '').trim(),
    extra_price: Number((o && o.extra_price) || 0) || 0,
    is_recommend: !!(o && o.is_recommend),
    stock_status: Number(o && o.stock_status) === 0 ? 0 : 1,
    sort: o && o.sort != null ? Number(o.sort) : sort || 0
  }
}

export default {
  name: 'ProductManagement',
  data() {
    const validateTotalMeals = (rule, value, callback) => {
      if (this.form.category !== '随心订') {
        callback()
        return
      }
      const n = Math.floor(Number(value))
      if (!Number.isFinite(n) || n < 1) {
        callback(new Error('总购买餐次须为 1 及以上的正整数'))
        return
      }
      callback()
    }
    return {
      categoryOptions: FALLBACK_CATEGORY_OPTIONS.slice(),
      specConfigCategories: FALLBACK_SPEC_CONFIG_CATEGORIES.slice(),
      packageTypeConfig: [
        { value: 'week', label: '周餐', enabled: true },
        { value: 'month', label: '月餐', enabled: true },
        { value: 'quarter', label: '季餐', enabled: true },
        { value: 'year', label: '年餐', enabled: true },
        { value: 'custom', label: '自定义', enabled: true }
      ],
      specTemplates: [],
      categoryFilter: '',
      products: [],
      dialogVisible: false,
      dialogMode: 'create',
      saving: false,
      currentId: null,
      _dragGroupIndex: null,
      form: {
        name: '',
        category: '',
        price: '',
        stock: '',
        sale_status: 'on',
        image_url: '',
        description: '',
        product_tags: '',
        main_ingredient: '',
        weight_text: '',
        calories_text: '',
        tips: '',
        support_pickup: true,
        support_delivery: false,
        spec_groups: [],
        subscription_meals: ['lunch', 'dinner'],
        subscription_meals_mode: 'both',
        subscription_total_meals: 10,
        subscription_meal_tiers_text: '',
        subscription_package_type: 'week',
        unit_meal_price: '',
        subscription_allow_single_meal: false,
        meal_lunch: true,
        meal_dinner: true
      },
      presetDefaults: {
        week: { label: '周餐', base_meals: 5 },
        month: { label: '月餐', base_meals: 22 },
        quarter: { label: '季餐', base_meals: 66 },
        year: { label: '年餐', base_meals: 264 },
        custom: { label: '自定义', base_meals: 10 }
      },
      presetDialogVisible: false,
      presetForm: { week: 5, month: 22, quarter: 66, year: 264 },
      formFormKey: 0,
      rules: {
        name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        category: [{ required: true, message: '请选择分类', trigger: 'change' }],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          { pattern: /^\d+(\.\d+)?$/, message: '价格格式不正确', trigger: 'blur' }
        ],
        stock: [{ pattern: /^\d+$/, message: '库存必须是整数', trigger: 'blur' }],
        subscription_total_meals: [{ validator: validateTotalMeals, trigger: 'change' }]
      }
    }
  },
  computed: {
    assetOrigin() {
      const base =
        (this.$api && this.$api.defaults && this.$api.defaults.baseURL) ||
        (typeof window !== 'undefined' ? window.location.origin + '/api' : '')
      return String(base || '').replace(/\/api\/?$/, '') || (typeof window !== 'undefined' ? window.location.origin : '')
    },
    isSpecConfigCategory() {
      return this.specConfigCategories.includes(String(this.form.category || '').trim())
    },
    enabledSpecTemplates() {
      return (this.specTemplates || []).filter((t) => t && t.enabled !== false && t.group_name)
    },
    categoryFilterOptions() {
      const configured = new Set(
        (this.categoryOptions || []).map((c) => String(c || '').trim()).filter(Boolean)
      )
      const fromProducts = new Set()
      ;(this.products || []).forEach((p) => {
        const c = String((p && p.category) || '').trim()
        if (c) fromProducts.add(c)
      })
      const options = []
      // 配置中的分类优先，按配置顺序
      ;(this.categoryOptions || []).forEach((c) => {
        const name = String(c || '').trim()
        if (!name) return
        options.push({ value: name, label: name, orphan: false })
      })
      // 商品仍在用、但配置已删除的分类（残留）
      Array.from(fromProducts)
        .filter((c) => !configured.has(c))
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .forEach((name) => {
          options.push({
            value: name,
            label: name + '（配置已移除）',
            orphan: true
          })
        })
      return options
    },
    filteredProducts() {
      const cat = String(this.categoryFilter || '').trim()
      if (!cat) return this.products || []
      return (this.products || []).filter((p) => String((p && p.category) || '').trim() === cat)
    },
    packageTypeOptions() {
      const p = this.presetDefaults || {}
      const coef = this.mealsPayloadFromCheckboxes().length >= 2 ? 2 : 1
      const meta = (key, fallback) => {
        const base = (p[key] && p[key].base_meals) || fallback
        return `${base} 天 × ${coef} = ${base * coef} 餐`
      }
      const fallbackBase = { week: 5, month: 22, quarter: 66, year: 264 }
      const configured = (this.packageTypeConfig || []).filter((x) => x && x.enabled !== false)
      const list = configured.length
        ? configured
        : [
            { value: 'week', label: '周餐' },
            { value: 'month', label: '月餐' },
            { value: 'quarter', label: '季餐' },
            { value: 'year', label: '年餐' },
            { value: 'custom', label: '自定义' }
          ]
      return list.map((item) => {
        const value = item.value
        const label = (p[value] && p[value].label) || item.label || value
        if (value === 'custom') {
          return { value, label, meta: '手动填写总餐次' }
        }
        return {
          value,
          label,
          meta: meta(value, fallbackBase[value] || 5)
        }
      })
    }
  },
  mounted() {
    this.getProducts();
    this.loadMgmtConfig();
  },
  methods: {
    resolveImg(url) {
      const s = String(url || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s) || /^data:/i.test(s)) return s
      if (s.startsWith('/')) return this.assetOrigin + s
      return s
    },
    async loadMgmtConfig() {
      try {
        const resp = await this.$api.get('/products/mgmt-config');
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data;
          if (Array.isArray(d.categories) && d.categories.length) {
            this.categoryOptions = d.categories.slice();
          }
          if (Array.isArray(d.spec_config_categories) && d.spec_config_categories.length) {
            this.specConfigCategories = d.spec_config_categories.slice();
          }
          if (Array.isArray(d.subscription_package_types) && d.subscription_package_types.length) {
            this.packageTypeConfig = d.subscription_package_types.map((x) => ({ ...x }));
          }
          if (Array.isArray(d.spec_templates)) {
            this.specTemplates = d.spec_templates.map((t) => ({
              ...t,
              options: Array.isArray(t.options) ? t.options.map((o) => ({ ...o })) : []
            }));
          }
          if (d.subscription_presets) {
            this.presetDefaults = d.subscription_presets;
            this.presetForm = {
              week: d.subscription_presets.week.base_meals,
              month: d.subscription_presets.month.base_meals,
              quarter: d.subscription_presets.quarter.base_meals,
              year: d.subscription_presets.year.base_meals
            };
          }
          return;
        }
      } catch (_e) {
        /* fallback */
      }
      await this.loadPresetDefaults();
    },
    findSpecTemplate(groupName) {
      const name = String(groupName || '').trim();
      if (!name) return null;
      return this.enabledSpecTemplates.find((t) => t.group_name === name) || null;
    },
    optionCandidatesForGroup(group) {
      const tpl = this.findSpecTemplate(group && group.group_name);
      if (!tpl) return [];
      return (tpl.options || []).filter((o) => o && o.enabled !== false && o.option_name);
    },
    onSpecGroupNameChange(gIndex, val) {
      const group = this.form.spec_groups[gIndex];
      if (!group) return;
      const nextName = String(val || '').trim();
      const prevName = String(group._prev_group_name != null ? group._prev_group_name : '').trim();
      const hasRealOptions = (group.options || []).some((o) => String((o && o.option_name) || '').trim());
      // 仅在「切换为另一个板块」时带入模板；同名重选不覆盖已删选项
      if (nextName && nextName === prevName && hasRealOptions) {
        return;
      }
      this.$set(group, '_prev_group_name', nextName);
      const tpl = this.findSpecTemplate(val);
      if (!tpl) {
        this.$set(group, 'excluded_options', []);
        return;
      }
      this.$set(group, 'min_select', tpl.min_select != null ? tpl.min_select : 1);
      this.$set(
        group,
        'max_select',
        tpl.max_select == null || Number(tpl.max_select) <= 0 ? 0 : tpl.max_select
      );
      const opts = (tpl.options || [])
        .filter((o) => o && o.enabled !== false && o.option_name)
        .map((o, i) => optionFromTemplate(o, i));
      this.$set(group, 'options', opts.length ? opts : [emptySpecOption()]);
      this.$set(group, 'excluded_options', []);
      this.$message.success(`已带入板块「${tpl.group_name}」下 ${opts.length} 个选项`);
    },
    onSpecOptionNameChange(gIndex, oIndex, val) {
      const group = this.form.spec_groups[gIndex];
      if (!group || !group.options || !group.options[oIndex]) return;
      const cand = this.optionCandidatesForGroup(group).find((o) => o.option_name === val);
      if (!cand) return;
      const opt = group.options[oIndex];
      this.$set(opt, 'extra_price', Number(cand.extra_price) || 0);
      this.$set(opt, 'stock_status', Number(cand.stock_status) === 0 ? 0 : 1);
      this.$set(opt, 'is_recommend', !!cand.is_recommend);
      if (cand.option_image) this.$set(opt, 'option_image', cand.option_image);
      if (cand.sort != null) this.$set(opt, 'sort', Number(cand.sort));
    },
    onMealTiersTextChange() {
      const raw = String(this.form.subscription_meal_tiers_text || '')
      const parts = raw
        .split(/[,，\s]+/)
        .map((x) => Math.floor(Number(x)))
        .filter((n) => Number.isFinite(n) && n >= 1)
      this.form.subscription_meal_tiers_text = [...new Set(parts)].sort((a, b) => a - b).join(',')
    },
    selectPackageType(value) {
      if (this.form.subscription_package_type === value) return
      this.form.subscription_package_type = value
      this.onPackageTypeChange()
    },
    toggleMealSlot(slot) {
      if (slot === 'lunch') this.form.meal_lunch = !this.form.meal_lunch
      if (slot === 'dinner') this.form.meal_dinner = !this.form.meal_dinner
      this.onMealSlotsChange()
    },
    parseTagsInput(raw) {
      if (Array.isArray(raw)) {
        return raw.map((t) => String(t).trim()).filter(Boolean)
      }
      return String(raw || '')
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean)
    },
    tagsToInput(tags) {
      if (Array.isArray(tags)) return tags.filter(Boolean).join('，')
      return String(tags || '')
    },
    async loadPresetDefaults() {
      try {
        const resp = await this.$api.get('/products/subscription-preset-defaults');
        if (resp && resp.code === 200 && resp.data) {
          this.presetDefaults = resp.data;
          this.presetForm = {
            week: resp.data.week.base_meals,
            month: resp.data.month.base_meals,
            quarter: resp.data.quarter.base_meals,
            year: resp.data.year.base_meals
          };
        }
      } catch (_e) {
        /* 使用内置默认 */
      }
    },
    mealsPayloadFromCheckboxes() {
      const out = [];
      if (this.form.meal_lunch) out.push('lunch');
      if (this.form.meal_dinner) out.push('dinner');
      return out.length ? out : ['lunch'];
    },
    onMealSlotsChange() {
      if (!this.form.meal_lunch && !this.form.meal_dinner) {
        this.form.meal_lunch = true;
        this.$message.warning('至少选择一个餐次时段');
      }
      this.onPackageTypeChange();
    },
    async onPackageTypeChange() {
      const pkg = this.form.subscription_package_type;
      const coef = this.mealsPayloadFromCheckboxes().length >= 2 ? 2 : 1;
      if (pkg === 'custom') {
        if (!this.form.subscription_total_meals) this.form.subscription_total_meals = 10;
      } else {
        const base = (this.presetDefaults[pkg] && this.presetDefaults[pkg].base_meals) || 5;
        this.form.subscription_total_meals = Math.max(1, base * coef);
      }
      await this.recalcSubscriptionPrice();
    },
    async recalcSubscriptionPrice() {
      if (this.form.category !== '随心订') return;
      const unit = Number(this.form.unit_meal_price);
      if (!Number.isFinite(unit) || unit < 0) return;
      try {
        const resp = await this.$api.post('/products/calculate-subscription-price', {
          unit_meal_price: unit,
          subscription_total_meals: this.form.subscription_total_meals,
          subscription_meals: this.mealsPayloadFromCheckboxes(),
          subscription_package_type: this.form.subscription_package_type
        });
        if (resp && resp.code === 200 && resp.data) {
          this.form.price = String(resp.data.total_price);
          if (resp.data.subscription_total_meals != null) {
            this.form.subscription_total_meals = resp.data.subscription_total_meals;
          }
        }
      } catch (_e) {
        /* ignore */
      }
    },
    openPresetDialog() {
      this.presetDialogVisible = true;
    },
    async savePresetDefaults() {
      try {
        const payload = {
          week: { base_meals: Number(this.presetForm.week) },
          month: { base_meals: Number(this.presetForm.month) },
          quarter: { base_meals: Number(this.presetForm.quarter) },
          year: { base_meals: Number(this.presetForm.year) }
        };
        const resp = await this.$api.put('/products/subscription-preset-defaults', payload);
        if (resp && resp.code === 200) {
          this.presetDefaults = resp.data;
          this.$message.success('默认餐次基数已保存');
          this.presetDialogVisible = false;
          this.onPackageTypeChange();
        }
      } catch (e) {
        this.$message.error('保存失败');
      }
    },
    mealsModeFromProduct(meals) {
      const arr = Array.isArray(meals) ? meals : []
      let hasL = false
      let hasD = false
      arr.forEach((s) => {
        const t = String(s).trim().toLowerCase()
        const z = String(s).trim()
        if (t === 'lunch' || z === '午餐') hasL = true
        if (t === 'dinner' || z === '晚餐') hasD = true
      })
      if (hasL && hasD) return 'both'
      if (hasL) return 'lunch'
      if (hasD) return 'dinner'
      return 'both'
    },
    mealsModeToPayload(mode) {
      if (mode === 'lunch') return ['lunch']
      if (mode === 'dinner') return ['dinner']
      return ['lunch', 'dinner']
    },
    resolveTotalMealsFromProduct(product) {
      const direct = Math.floor(Number(product && product.subscription_total_meals))
      if (direct >= 1) return direct
      const meals = Array.isArray(product && product.subscription_meals) ? product.subscription_meals : ['lunch', 'dinner']
      let days = 5
      if (product && product.subscription_fixed_workdays != null) {
        days = parseInt(String(product.subscription_fixed_workdays), 10) || 5
      } else if (product && Array.isArray(product.subscription_day_options) && product.subscription_day_options.length) {
        days = parseInt(String(product.subscription_day_options[0]), 10) || 5
      }
      return Math.max(1, days * Math.max(1, meals.length))
    },
    onTotalMealsChange(val) {
      const n = Math.floor(Number(val))
      if (!Number.isFinite(n) || n < 1) {
        this.$message.warning('总购买餐次须为 1 及以上的正整数')
        this.$nextTick(() => {
          this.form.subscription_total_meals = 1
        })
      }
    },
    async getProducts() {
      try {
        const data = await fetchProducts();
        if (data && data.code === 200 && Array.isArray(data.data)) {
          this.products = data.data;
          localStorage.setItem('dashboard_product_count', String(this.products.length));
          return;
        }
        throw new Error('invalid response');
      } catch (e) {
        this.products = [
          {
            id: '1',
            name: '测试套餐A',
            price: 35,
            category: '随心订',
            stock: 99,
            sale_status: 'on',
            createdAt: '2026-03-10 09:00:00',
            image_url: 'https://picsum.photos/200/200?random=11',
            subscription_meals: ['lunch', 'dinner'],
            subscription_total_meals: 10
          },
          {
            id: '2',
            name: '测试轻食B',
            price: 28,
            category: '小食',
            stock: 88,
            sale_status: 'on',
            createdAt: '2026-03-10 10:00:00',
            image_url: 'https://picsum.photos/200/200?random=12'
          }
        ];
        localStorage.setItem('dashboard_product_count', String(this.products.length));
        this.$message.warning(
          `${formatApiError(e, '商品接口不可用')}；已加载测试数据`
        );
      }
    },
    normalizeSaleStatus(raw) {
      const s = String(raw || '').trim().toLowerCase()
      if (s === 'off' || s === 'sold_out' || s === 'on') return s
      if (s === '0' || s === 'false' || s === 'down' || s === 'offline') return 'off'
      if (s === 'soldout' || s === 'sold-out' || s === 'out') return 'sold_out'
      return 'on'
    },
    saleStatusLabel(raw) {
      const s = this.normalizeSaleStatus(raw)
      if (s === 'off') return '已下架'
      if (s === 'sold_out') return '售罄'
      return '上架中'
    },
    saleStatusTagType(raw) {
      const s = this.normalizeSaleStatus(raw)
      if (s === 'off') return 'info'
      if (s === 'sold_out') return 'warning'
      return 'success'
    },
    async setSaleStatus(row, status) {
      if (!row || !row.id) return
      const next = this.normalizeSaleStatus(status)
      if (this.normalizeSaleStatus(row.sale_status) === next) return
      try {
        await updateProduct(row.id, { sale_status: next })
        this.$set(row, 'sale_status', next)
        this.$message.success(this.saleStatusLabel(next) + '成功')
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '状态更新失败'
        this.$message.error(msg)
      }
    },
    async handleImageUpload(param) {
      if (!param.file) return;
      const formData = new FormData();
      formData.append('file', param.file);
      try {
        const resp = await this.$api.post('/products/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          this.form.image_url = resp.data.url;
          this.$message.success('图片上传成功');
        } else {
          this.$message.error('图片上传失败');
        }
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '图片上传失败';
        this.$message.error(msg);
      } finally {
        param.onSuccess && param.onSuccess();
      }
    },
    openCreate() {
      this.loadMgmtConfig();
      this.dialogMode = 'create';
      this.currentId = null;
      this.formFormKey += 1;
      this.form = {
        name: '',
        category: (this.categoryOptions && this.categoryOptions[0]) || '随心订',
        price: '',
        stock: '',
        sale_status: 'on',
        image_url: '',
        description: '',
        product_tags: '',
        main_ingredient: '',
        weight_text: '',
        calories_text: '',
        tips: '',
        support_pickup: true,
        support_delivery: false,
        spec_groups: [],
        subscription_meals: ['lunch', 'dinner'],
        subscription_meals_mode: 'both',
        subscription_total_meals: 10,
        subscription_meal_tiers_text: '',
        subscription_package_type: 'week',
        unit_meal_price: '',
        subscription_allow_single_meal: false,
        meal_lunch: true,
        meal_dinner: true
      };
      this.onPackageTypeChange();
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.productForm && this.$refs.productForm.clearValidate());
    },
    editProduct(product) {
      this.loadMgmtConfig();
      this.dialogMode = 'edit';
      this.currentId = product.id;
      this.formFormKey += 1;
      const cat = product.category || ''
      const meals = Array.isArray(product.subscription_meals) && product.subscription_meals.length
        ? product.subscription_meals.slice()
        : ['lunch', 'dinner']
      const hasL = meals.some((m) => String(m).toLowerCase() === 'lunch' || m === '午餐')
      const hasD = meals.some((m) => String(m).toLowerCase() === 'dinner' || m === '晚餐')
      const tags = Array.isArray(product.product_tags)
        ? product.product_tags.slice()
        : []
      const groups = Array.isArray(product.spec_groups)
        ? product.spec_groups.map((g, gi) => {
            let maxSelect =
              g.max_select != null && g.max_select !== '' ? Number(g.max_select) : null
            // 旧版「多选+加减」且未设上限 → 转为最多 99，统一圆角按钮累加
            if ((maxSelect == null || maxSelect <= 0) && g.select_type === 'counter') {
              maxSelect = 99
            }
            return {
              group_name: g.group_name || '',
              _prev_group_name: g.group_name || '',
              min_select: g.min_select != null ? Number(g.min_select) : 1,
              max_select: maxSelect != null && maxSelect > 0 ? maxSelect : null,
              select_type: 'radio',
              sort: g.sort != null ? Number(g.sort) : gi,
              excluded_options: Array.isArray(g.excluded_options)
                ? g.excluded_options.map((x) => String(x || '').trim()).filter(Boolean)
                : [],
              options: Array.isArray(g.options)
                ? g.options.map((o, oi) => ({
                    option_name: o.option_name || '',
                    option_image: o.option_image || '',
                    extra_price: Number(o.extra_price || 0),
                    is_recommend: !!(o.is_recommend === true || o.is_recommend === 1),
                    stock_status: Number(o.stock_status) === 0 ? 0 : 1,
                    sort: o.sort != null ? Number(o.sort) : oi
                  }))
                : []
            }
          })
        : []
      this.form = {
        name: product.name || '',
        category:
          cat === '增肌随心碗'
            ? '超级碗'
            : (this.categoryOptions || []).includes(cat)
              ? cat
              : ((this.categoryOptions && this.categoryOptions[0]) || '随心订'),
        price: product.price === undefined ? '' : String(product.price),
        stock: product.stock === undefined ? '' : String(product.stock),
        sale_status: this.normalizeSaleStatus(product.sale_status),
        image_url: product.image_url || '',
        description: product.description || '',
        product_tags: this.tagsToInput(tags),
        main_ingredient: product.main_ingredient || '',
        weight_text: product.weight_text || '',
        calories_text: product.calories_text || '',
        tips: product.tips || '',
        support_pickup: !(
          product.support_pickup === false ||
          product.support_pickup === 0 ||
          product.support_pickup === '0'
        ),
        support_delivery: !(
          product.support_delivery === false ||
          product.support_delivery === 0 ||
          product.support_delivery === '0'
        ),
        spec_groups: groups,
        subscription_meals: meals,
        subscription_meals_mode: this.mealsModeFromProduct(product.subscription_meals),
        subscription_total_meals: this.resolveTotalMealsFromProduct(product),
        subscription_meal_tiers_text: Array.isArray(product.subscription_meal_tiers)
          ? product.subscription_meal_tiers.join(',')
          : '',
        subscription_package_type: product.subscription_package_type || 'custom',
        unit_meal_price: product.unit_meal_price != null ? String(product.unit_meal_price) : '',
        subscription_allow_single_meal: !!(
          product.subscription_allow_single_meal === true ||
          product.subscription_allow_single_meal === 1
        ),
        meal_lunch: hasL || (!hasL && !hasD),
        meal_dinner: hasD || (!hasL && !hasD)
      };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.productForm && this.$refs.productForm.clearValidate());
    },
    addSpecGroup() {
      if (!Array.isArray(this.form.spec_groups)) this.form.spec_groups = []
      this.form.spec_groups.push(emptySpecGroup())
    },
    removeSpecGroup(index) {
      this.form.spec_groups.splice(index, 1)
    },
    moveSpecGroup(index, delta) {
      const next = index + delta
      if (next < 0 || next >= this.form.spec_groups.length) return
      const arr = this.form.spec_groups
      const tmp = arr[index]
      this.$set(arr, index, arr[next])
      this.$set(arr, next, tmp)
    },
    onGroupDragStart(index, e) {
      this._dragGroupIndex = index
      try {
        e.dataTransfer.setData('text/plain', String(index))
      } catch (_err) { /* ignore */ }
    },
    onGroupDrop(index) {
      const from = this._dragGroupIndex
      this._dragGroupIndex = null
      if (from == null || from === index) return
      const arr = this.form.spec_groups
      const item = arr.splice(from, 1)[0]
      arr.splice(index, 0, item)
    },
    addSpecOption(gIndex) {
      const group = this.form.spec_groups[gIndex]
      if (!group) return
      if (!Array.isArray(group.options)) this.$set(group, 'options', [])
      group.options.push(emptySpecOption())
    },
    removeSpecOption(gIndex, oIndex) {
      const group = this.form.spec_groups[gIndex]
      if (!group || !Array.isArray(group.options)) return
      const removed = group.options[oIndex]
      const name = String((removed && removed.option_name) || '').trim()
      group.options.splice(oIndex, 1)
      if (!name) return
      if (!Array.isArray(group.excluded_options)) this.$set(group, 'excluded_options', [])
      if (!group.excluded_options.includes(name)) {
        group.excluded_options.push(name)
      }
    },
    async handleOptionImageUpload(param, gIndex, oIndex) {
      if (!param.file) return
      const formData = new FormData()
      formData.append('file', param.file)
      try {
        const resp = await this.$api.post('/products/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          const group = this.form.spec_groups[gIndex]
          if (group && group.options && group.options[oIndex]) {
            this.$set(group.options[oIndex], 'option_image', resp.data.url)
          }
          this.$message.success('选项图片上传成功')
        } else {
          this.$message.error('图片上传失败')
        }
      } catch (e) {
        this.$message.error('图片上传失败')
      } finally {
        param.onSuccess && param.onSuccess()
      }
    },
    buildSpecGroupsPayload() {
      return (this.form.spec_groups || [])
        .map((g, gi) => {
          const options = (g.options || [])
            .map((o, oi) => ({
              option_name: String(o.option_name || '').trim(),
              option_image: String(o.option_image || '').trim(),
              extra_price: Number(o.extra_price || 0) || 0,
              is_recommend: !!o.is_recommend,
              stock_status: Number(o.stock_status) === 0 ? 0 : 1,
              sort: o.sort != null ? Number(o.sort) : oi
            }))
            .filter((o) => o.option_name)
          const saved = new Set(options.map((o) => o.option_name))
          const excluded = [
            ...new Set(
              (Array.isArray(g.excluded_options) ? g.excluded_options : [])
                .map((x) => String(x || '').trim())
                .filter((n) => n && !saved.has(n))
            )
          ]
          return {
            group_name: String(g.group_name || '').trim(),
            min_select: Math.max(0, Math.floor(Number(g.min_select != null ? g.min_select : 1))),
            max_select:
              g.max_select == null || g.max_select === '' || Number(g.max_select) <= 0
                ? null
                : Math.floor(Number(g.max_select)),
            select_type: 'radio',
            sort: gi,
            excluded_options: excluded,
            options
          }
        })
        .filter((g) => g.group_name)
    },
    async submit() {
      this.$refs.productForm.validate(async (valid) => {
        if (!valid) return;
        if (this.form.category === '随心订') {
          const n = Math.floor(Number(this.form.subscription_total_meals))
          if (!Number.isFinite(n) || n < 1) {
            this.$message.error('总购买餐次须为 1 及以上的正整数')
            return
          }
        }
        if (this.isSpecConfigCategory) {
          if (!this.form.support_pickup && !this.form.support_delivery) {
            this.$message.error('请至少选择一种履约方式（门店自提或外送）')
            return
          }
          const groups = this.buildSpecGroupsPayload()
          for (let i = 0; i < groups.length; i++) {
            if (!groups[i].options.length) {
              this.$message.error(`板块「${groups[i].group_name}」请至少添加一个选项`)
              return
            }
          }
        }
        this.saving = true;
        try {
          const payload = {
            name: this.form.name,
            category: this.form.category,
            price: Number(this.form.price),
            stock: this.form.stock === '' ? 0 : Number(this.form.stock),
            sale_status: this.normalizeSaleStatus(this.form.sale_status),
            image_url: this.form.image_url,
            description: this.form.description
          };
          if (this.form.category === '随心订') {
            payload.subscription_meals = this.mealsPayloadFromCheckboxes()
            payload.subscription_total_meals = Math.floor(Number(this.form.subscription_total_meals))
            payload.subscription_package_type = this.form.subscription_package_type
            this.onMealTiersTextChange()
            const tiers = String(this.form.subscription_meal_tiers_text || '')
              .split(/[,，\s]+/)
              .map((x) => Math.floor(Number(x)))
              .filter((n) => Number.isFinite(n) && n >= 1)
            payload.subscription_meal_tiers = [...new Set(tiers)].sort((a, b) => a - b)
            if (this.form.unit_meal_price !== '') {
              payload.unit_meal_price = Number(this.form.unit_meal_price)
            }
            payload.subscription_allow_single_meal = !!this.form.subscription_allow_single_meal
            await this.recalcSubscriptionPrice()
            payload.price = Number(this.form.price)
          }
          if (this.isSpecConfigCategory) {
            payload.product_tags = this.parseTagsInput(this.form.product_tags)
            payload.main_ingredient = this.form.main_ingredient || ''
            payload.weight_text = this.form.weight_text || ''
            payload.calories_text = this.form.calories_text || ''
            payload.tips = this.form.tips || ''
            payload.support_pickup = !!this.form.support_pickup
            payload.support_delivery = !!this.form.support_delivery
            payload.spec_groups = this.buildSpecGroupsPayload()
          } else {
            payload.spec_groups = []
          }

          if (this.dialogMode === 'create') {
            await createProduct(payload);
            this.$message.success('添加成功');
          } else {
            await updateProduct(this.currentId, payload);
            this.$message.success('更新成功');
          }
          this.dialogVisible = false;
          await this.getProducts();
        } catch (e) {
          const msg =
            (e && e.response && e.response.data && e.response.data.message) ||
            (e && e.message) ||
            ''
          this.$message.error(
            msg || (this.dialogMode === 'create' ? '添加失败' : '更新失败')
          );
        } finally {
          this.saving = false;
        }
      });
    },
    async deleteProduct(productId) {
      try {
        await this.$confirm('确定删除该商品吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        await deleteProductApi(productId);
        this.$message.success('删除成功');
        await this.getProducts();
      } catch (e) {
        // cancelled or error
      }
    }
  }
}
</script>

<style scoped>
.product-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pm-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  /* 给悬停描边留出空间，避免被表格单元格裁切 */
  padding: 2px 0;
}

.pm-row-actions .el-button {
  margin-left: 0 !important;
  min-width: 72px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease,
    box-shadow 0.18s ease;
}

.pm-row-actions .el-button i {
  margin-right: 2px;
  font-size: 13px;
}

.pm-action-edit:hover,
.pm-action-edit:focus {
  background: #f3fbf6 !important;
  border-color: #2bb673 !important;
  color: #047857 !important;
  box-shadow: 0 2px 8px rgba(43, 182, 115, 0.16) !important;
}

.pm-action-status:hover,
.pm-action-status:focus {
  background: #f0f7ff !important;
  border-color: #3b82f6 !important;
  color: #1d4ed8 !important;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.16) !important;
}

.pm-action-del:hover,
.pm-action-del:focus {
  background: #e11d48 !important;
  border-color: #e11d48 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(225, 29, 72, 0.22) !important;
}

/* Element 表格默认 overflow:hidden，悬停描边会被裁掉上边 */
.product-table .el-table__body td .cell {
  overflow: visible;
}

.product-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.filter-label {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}

.filter-summary {
  font-size: 12px;
  color: #94a3b8;
}

.image-slot {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  border-radius: 8px;
}

.product-form {
  margin: 0;
}

.pf-section {
  margin-bottom: 16px;
  padding: 18px 18px 8px;
  border: 1px solid #eef2f0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.03);
}

.pf-section-accent {
  background: linear-gradient(180deg, #f5faf7 0%, #ffffff 48%);
  border-color: #dceee4;
}

.pf-section-head {
  margin-bottom: 14px;
}

.pf-section-head h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 650;
  color: #111;
  letter-spacing: 0.01em;
}

.pf-section-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.pf-grid {
  display: grid;
  gap: 0 16px;
}

.pf-grid-2 {
  grid-template-columns: 1fr 1fr;
}

.pkg-type-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
}

.pkg-type-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 10px 12px;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.pkg-type-card:hover {
  border-color: #bbb;
}

.pkg-type-card.active {
  border-color: #2ecc71;
  background: #f0faf4;
  box-shadow: none;
}

.pkg-type-name {
  font-size: 13px;
  font-weight: 600;
  color: #222;
}

.pkg-type-card.active .pkg-type-name {
  color: #1a9b55;
}

.pkg-type-meta {
  font-size: 11px;
  color: #888;
  line-height: 1.35;
}

.pkg-type-card.active .pkg-type-meta {
  color: #5a8f70;
}

.pf-preset-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: -4px 0 14px;
}

.pf-preset-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f3f3;
  color: #555;
  font-size: 11px;
}

.pf-link-btn {
  color: #111 !important;
  font-weight: 500;
  padding: 0 !important;
}

.meal-slot-toggles {
  display: flex;
  gap: 10px;
}

.meal-slot-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  background: #fff;
  color: #444;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.meal-slot-btn.active {
  border-color: #2ecc71;
  background: #f0faf4;
  color: #1a9b55;
}

.pf-price-pair .el-form-item {
  margin-bottom: 14px;
}

.pf-readonly-meals-value {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
}

.pf-tiers-item {
  grid-column: 1 / -1;
}

.pf-price-summary {
  margin: 4px 0 12px;
  padding: 14px 16px 6px;
  border-radius: 12px;
  background: #f5faf7;
  border: 1px solid #d8efe3;
  color: #222;
}

.pf-price-main {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.pf-price-label {
  font-size: 12px;
  color: #6b7c72;
}

.pf-price-value {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1a9b55;
}

.pf-price-formula {
  margin-top: 4px;
  font-size: 12px;
  color: #7a8a80;
}

.pf-price-override {
  margin-top: 12px;
  margin-bottom: 8px;
}

.pf-price-override >>> .el-form-item__label {
  color: #555;
}

.pf-media-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 18px;
  align-items: start;
}

.pf-upload-item {
  margin-bottom: 0;
}

.avatar-uploader {
  display: inline-block;
}

.avatar {
  width: 120px;
  height: 120px;
  display: block;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #ebebeb;
}

.avatar-placeholder {
  width: 120px;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px dashed #cfcfcf;
  border-radius: 12px;
  color: #888;
  background: #fafafa;
  font-size: 12px;
}

.avatar-placeholder i {
  font-size: 22px;
}

.pf-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
}

.pf-base-price-item .el-form-item__label {
  font-weight: 600;
}

.base-price-field {
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 320px;
  height: 42px;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.base-price-field--full {
  max-width: none;
}

.base-price-field--readonly {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.base-price-field--disabled {
  background: #f8fafc;
  opacity: 0.92;
}

.base-price-field:focus-within {
  border-color: #2bb673;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.15);
}

.base-price-field--readonly:focus-within,
.base-price-field--disabled:focus-within {
  border-color: #e2e8f0;
  box-shadow: none;
}

.base-price-prefix,
.base-price-suffix {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 0 12px;
  background: #f5faf7;
  color: #2bb673;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  line-height: 1;
}

.base-price-suffix {
  color: #64748b;
  font-weight: 600;
  font-size: 13px;
  background: #f8fafc;
  border-left: 1px solid #eef2f0;
}

.base-price-prefix {
  border-right: 1px solid #eef2f0;
}

.base-price-input {
  flex: 1;
  height: 100%;
}

.base-price-input >>> .el-input {
  height: 100%;
}

.base-price-input >>> .el-input__inner {
  border: none !important;
  height: 100% !important;
  line-height: 40px;
  border-radius: 0;
  box-shadow: none !important;
  padding-left: 12px;
  padding-right: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  background: transparent;
}

.base-price-number {
  flex: 1;
  width: auto !important;
  height: 100%;
  line-height: 40px;
}

.base-price-number >>> .el-input__inner {
  border: none !important;
  height: 40px !important;
  line-height: 40px;
  border-radius: 0;
  box-shadow: none !important;
  text-align: left;
  padding-left: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  background: transparent;
}

.base-price-number >>> .el-input-number__increase,
.base-price-number >>> .el-input-number__decrease {
  width: 32px;
  background: #f8fafc;
  border-color: #eef2f0;
  color: #64748b;
}

.base-price-hint {
  margin-top: 8px;
  color: #64748b;
}

.product-form-dialog .pf-field-input >>> .el-input__inner {
  height: 42px;
  line-height: 42px;
  border-radius: 12px;
  border-color: #e2e8f0;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.product-form-dialog .pf-field-input >>> .el-input__inner:focus {
  border-color: #2bb673;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.15);
}

.pf-fulfillment-item .fulfillment-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  min-height: 40px;
}

.pf-fulfillment-item .fulfillment-check {
  margin-right: 0 !important;
  height: 36px;
  padding: 0 14px 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: inline-flex !important;
  align-items: center;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.pf-fulfillment-item .fulfillment-check.is-checked {
  border-color: #2bb673;
  background: #f3fbf6;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.12);
}

.pf-fulfillment-item .fulfillment-check >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.pf-fulfillment-item .fulfillment-check >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: #2bb673 !important;
  border-color: #2bb673 !important;
}

.pf-fulfillment-item .fulfillment-check >>> .el-checkbox__input.is-focus .el-checkbox__inner,
.pf-fulfillment-item .fulfillment-check >>> .el-checkbox__inner:hover {
  border-color: #2bb673 !important;
}

.pf-fulfillment-item .fulfillment-check >>> .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #047857 !important;
  font-weight: 600;
}

.pf-fulfillment-item .fulfillment-check >>> .el-checkbox__label {
  color: #475569;
  font-weight: 500;
  padding-left: 8px;
}

.pf-section-spec {
  background: linear-gradient(180deg, #f4faf6 0%, #f8faf9 100%);
  border: 1px solid #dceee4;
  border-radius: 18px;
  padding: 20px 18px 20px;
}

.spec-meta-grid {
  margin-bottom: 4px;
}

.spec-tips-item {
  grid-column: 1 / -1;
}

.spec-group-card {
  margin-bottom: 16px;
  padding: 18px 18px 16px;
  border: 1px solid #e6ece8;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.045);
  cursor: grab;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.spec-group-card:hover {
  border-color: #b7dfc6;
  box-shadow: 0 10px 28px rgba(43, 182, 115, 0.1);
}

.spec-group-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f3f1;
}

.spec-group-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.spec-group-badge {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  background: linear-gradient(135deg, #2bb673 0%, #34d399 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(43, 182, 115, 0.28);
}

.spec-group-title-text {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.spec-group-index {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.spec-group-style-tag {
  font-size: 11px;
  font-weight: 600;
  color: #059669;
  background: rgba(43, 182, 115, 0.12);
  padding: 4px 10px;
  border-radius: 999px;
}

.spec-group-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.spec-icon-btn {
  border: 1px solid #e5e9e6;
  background: #f8faf9;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.spec-icon-btn:hover:not(:disabled) {
  border-color: #cfead9;
  background: #f0faf4;
  color: #059669;
}

.spec-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.spec-icon-btn.danger {
  color: #e11d48;
  border-color: #fecdd3;
  background: #fff1f2;
}

.spec-icon-btn.danger:hover:not(:disabled) {
  background: #ffe4e6;
  border-color: #fda4af;
  color: #be123c;
}

.spec-rule-hint {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f5faf7;
  border: 1px dashed #cfead9;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}

.spec-full-number {
  width: 100%;
}

.spec-options-panel {
  margin-top: 6px;
  padding: 14px;
  border-radius: 14px;
  background: #f7faf8;
  border: 1px solid #e8f0eb;
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
}

.spec-options-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}

.spec-options-title {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.spec-options-count {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  background: rgba(43, 182, 115, 0.12);
  padding: 2px 8px;
  border-radius: 999px;
}

.spec-add-opt-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
}

.spec-option-card {
  background: #fff;
  border: 1px solid #e8eee9;
  border-radius: 14px;
  padding: 14px 14px 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
}

.spec-option-card:last-of-type {
  margin-bottom: 0;
}

.spec-option-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.spec-option-index {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.02em;
}

.spec-option-grid {
  display: grid;
  grid-template-columns: minmax(140px, 1.4fr) minmax(110px, 0.8fr) minmax(90px, 0.55fr) minmax(100px, 0.7fr) 88px 76px;
  gap: 12px 10px;
  align-items: end;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.spec-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.spec-field label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  line-height: 1.2;
}

.spec-field-flags {
  align-items: stretch;
}

.spec-field-image {
  align-items: stretch;
  max-width: 76px;
  overflow: hidden;
}

.spec-price-wrap {
  display: flex;
  align-items: stretch;
  border: 1px solid #dcdfe6;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.spec-price-wrap:focus-within {
  border-color: #2bb673;
  box-shadow: 0 0 0 3px rgba(43, 182, 115, 0.12);
}

.spec-price-yen {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  background: #f5faf7;
  color: #2bb673;
  font-weight: 700;
  font-size: 13px;
  border-right: 1px solid #ebeef5;
}

.spec-price-input {
  flex: 1;
  width: 100% !important;
}

.spec-price-input >>> .el-input__inner,
.spec-sort-input >>> .el-input__inner,
.spec-full-number >>> .el-input__inner {
  border: none !important;
  border-radius: 0 !important;
  height: 36px;
  line-height: 36px;
}

.spec-price-input >>> .el-input-number,
.spec-sort-input {
  width: 100%;
}

.spec-sort-input {
  width: 100% !important;
}

.spec-sort-input >>> .el-input__inner {
  border: 1px solid #dcdfe6 !important;
  border-radius: 10px !important;
  height: 36px;
  line-height: 36px;
  text-align: left;
  padding-left: 12px;
}

.spec-sort-input >>> .el-input-number__increase,
.spec-sort-input >>> .el-input-number__decrease,
.spec-price-input >>> .el-input-number__increase,
.spec-price-input >>> .el-input-number__decrease,
.spec-full-number >>> .el-input-number__increase,
.spec-full-number >>> .el-input-number__decrease {
  width: 28px;
  background: #f8faf9;
  border-color: #e8eee9;
  color: #64748b;
}

.spec-stock-select {
  width: 100%;
}

.spec-stock-select >>> .el-input__inner {
  height: 36px;
  line-height: 36px;
  border-radius: 8px;
}

.spec-recommend-chip {
  height: 36px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.spec-recommend-chip i {
  font-size: 15px;
  line-height: 1;
}

.spec-recommend-chip:hover {
  border-color: #f0c14b;
  color: #b45309;
  background: #fffbeb;
}

.spec-recommend-chip.active {
  border-color: #f5c542;
  background: linear-gradient(180deg, #fff8e6 0%, #fff3cd 100%);
  color: #a16207;
  box-shadow: 0 0 0 3px rgba(245, 197, 66, 0.18);
}

.spec-recommend-chip.active i {
  color: #eab308;
}

.spec-opt-media {
  position: relative;
  display: flex;
  align-items: center;
  width: 64px;
  max-width: 100%;
  flex-shrink: 0;
}

.spec-opt-upload {
  display: block;
  width: 64px;
  max-width: 100%;
}

.spec-opt-upload >>> .el-upload {
  display: block;
  width: 64px;
  max-width: 100%;
}

.spec-opt-upload-btn {
  width: 64px;
  height: 48px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 1px dashed #b7dfc6;
  border-radius: 10px;
  background: #f5faf7;
  color: #059669;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.spec-opt-upload-btn:hover {
  border-color: #2bb673;
  background: #ecf9f1;
  color: #047857;
}

.spec-opt-thumb-wrap {
  position: relative;
  width: 64px;
  height: 48px;
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  background: #f8fafc;
  box-sizing: border-box;
}

.spec-opt-thumb {
  width: 64px;
  height: 48px;
  max-width: 100%;
  object-fit: cover;
  display: block;
}

.spec-opt-thumb-tip {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.spec-opt-thumb-wrap:hover .spec-opt-thumb-tip {
  opacity: 1;
}

.spec-opt-clear {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35);
}

.spec-opt-clear i {
  font-size: 10px;
}

.spec-empty-hint {
  margin: 8px 0 0;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  padding: 16px 8px;
}

.spec-add-group-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
  padding: 16px;
  border: 1.5px dashed #2bb673;
  border-radius: 16px;
  background: rgba(43, 182, 115, 0.06);
  color: #059669;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}

.spec-add-group-btn:hover {
  background: rgba(43, 182, 115, 0.12);
  box-shadow: 0 8px 20px rgba(43, 182, 115, 0.14);
}

.spec-add-group-btn i {
  font-size: 16px;
  font-weight: 700;
}

@media (max-width: 900px) {
  .spec-option-grid {
    grid-template-columns: 1fr 1fr;
  }
  .spec-field-name {
    grid-column: 1 / -1;
  }
}

@media (max-width: 720px) {
  .pf-grid-2,
  .pf-media-row,
  .pkg-type-grid,
  .spec-option-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.product-form-dialog .el-dialog__body {
  max-height: min(74vh, 760px);
  overflow-y: auto;
  padding: 8px 22px 12px;
  background: #fafbfc;
}

.product-form-dialog .el-dialog__header {
  padding: 18px 22px 12px;
  border-bottom: 1px solid #eef2f0;
}

.product-form-dialog .el-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.product-form-dialog .el-dialog__footer {
  padding: 14px 22px 18px;
  border-top: 1px solid #eef2f0;
  background: #fff;
}

.product-form-dialog .el-form--label-top .el-form-item__label {
  padding: 0 0 6px;
  line-height: 1.2;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
}

.product-form-dialog .el-form-item {
  margin-bottom: 16px;
}

.product-form-dialog .el-input__inner,
.product-form-dialog .el-textarea__inner {
  border-radius: 10px;
}

.product-form-dialog .el-input-number {
  width: 100%;
}

.product-form-dialog .el-input-number .el-input__inner {
  text-align: left;
  padding-left: 12px;
}

.product-form-dialog .dialog-footer .btn-primary {
  border-radius: 10px;
  min-width: 108px;
  font-weight: 700;
}

.product-form-dialog .dialog-footer .btn-secondary {
  border-radius: 10px;
  min-width: 88px;
}

/* 分类筛选：下拉宽度与触发器一致，避免右侧多出一截 */
.pm-category-filter-dropdown.el-select-dropdown,
.pm-category-filter-dropdown {
  width: 180px !important;
  min-width: 180px !important;
  max-width: 180px !important;
}

.pm-category-filter-dropdown .el-select-dropdown__item {
  width: 100%;
  max-width: 100%;
  padding-right: 14px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pm-category-filter-dropdown .el-select-dropdown__item.selected::after {
  display: none !important;
}

.product-form-dialog .spec-options-panel {
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
}
</style>
